import type { TaskCard, TaskStatus } from '@witty-board/shared'

/** Orden fijo de las tres columnas (CLAUDE.md §6). */
export const TASK_STATUSES: readonly TaskStatus[] = ['todo', 'in_progress', 'done']

export function groupTasksByStatus(tasks: readonly TaskCard[]): Record<TaskStatus, TaskCard[]> {
  const groups: Record<TaskStatus, TaskCard[]> = { todo: [], in_progress: [], done: [] }
  for (const task of tasks) groups[task.status].push(task)
  for (const status of TASK_STATUSES) groups[status].sort((a, b) => a.position - b.position)
  return groups
}

/**
 * Reordena la lista en el cliente tras soltar una tarjeta, para que la
 * interfaz no espere a la respuesta de `POST /tasks/{id}/move` antes de
 * reflejar el cambio (actualización optimista, CLAUDE.md §6). `destIndex` es
 * el mismo índice destino que espera el backend.
 */
export function applyOptimisticMove(
  tasks: readonly TaskCard[],
  taskId: string,
  destStatus: TaskStatus,
  destIndex: number,
): TaskCard[] {
  const moved = tasks.find((task) => task.id === taskId)
  if (!moved) return [...tasks]

  const groups = groupTasksByStatus(tasks)
  groups[moved.status] = groups[moved.status].filter((task) => task.id !== taskId)

  const destList = [...groups[destStatus]]
  const clampedIndex = Math.max(0, Math.min(destIndex, destList.length))
  destList.splice(clampedIndex, 0, { ...moved, status: destStatus })
  groups[destStatus] = destList.map((task, index) => ({ ...task, position: index }))

  return TASK_STATUSES.flatMap((status) => groups[status])
}

/**
 * `task.position` se calcula en el backend dentro de `(projectId, status)`
 * (`services/position.ts`): no sabe nada de una columna que mezcla proyectos,
 * como la de "Mis tareas". Tras reordenar visualmente la columna completa con
 * `applyOptimisticMove` (que sí mezcla proyectos), esta función busca el índice
 * de la tarea movida entre solo sus vecinas del mismo proyecto — el índice que
 * de verdad entiende `POST /tasks/{id}/move` (ver docs/DECISIONS.md, fase 5).
 */
export function projectScopedPosition(
  tasksAfterMove: readonly TaskCard[],
  taskId: string,
  status: TaskStatus,
): number {
  const moved = tasksAfterMove.find((task) => task.id === taskId)
  if (!moved) return 0
  const sameProjectColumn = tasksAfterMove
    .filter((task) => task.status === status && task.projectId === moved.projectId)
    .sort((a, b) => a.position - b.position)
  const index = sameProjectColumn.findIndex((task) => task.id === taskId)
  return index === -1 ? 0 : index
}
